// frontend/src/constants/domain.js
// Vocabulary from the spec, plus the colours used consistently across the UI.

export const TASK_TYPES = ['PSG', 'GPDS', 'Other task', 'Training', 'Documentation', 'Meeting'];

// Task types that follow the short workflow instead of QC / customer approval:
// Yet to Start -> In Progress -> Validation -> Completed.
export const SIMPLE_TASK_TYPES = ['Training', 'Documentation', 'Meeting'];
export const isSimpleTaskType = (t) => SIMPLE_TASK_TYPES.includes(t);
export const SIMPLE_MAIN_FLOW_STEPS = ['Yet to Start', 'In Progress', 'Validation', 'Completed'];
export const SIMPLE_OWNER_STATUSES = ['Yet to Start', 'In Progress', 'On Hold', 'Validation'];

export const PRIORITIES = ['Low', 'Medium', 'High'];

export const ACTIVITY_TYPES = [
    'Product development',
    'Conversion',
    'CAD and drawing activity',
    'Data updation',
];

export const STATUSES = [
    'Yet to Start',
    'In Progress',
    'Internal - Peer QC',
    'Internal - Final QC',
    'Sent for Customer Approval',
    'PLM Approval',
    'Completed',
    'On Hold',
    'Cancelled',
    'Validation',
];

// Tickets ("data updation" items) skip QC entirely -- no Peer/Final QC stage.
export const TICKET_STATUSES = STATUSES.filter(
    s => s !== 'Internal - Peer QC' && s !== 'Internal - Final QC'
);

// The happy path, in order -- what the Stepper renders.
// On Hold / Cancelled sit outside it and are reachable from any live stage.
export const MAIN_FLOW_STEPS = [
    'Yet to Start',
    'In Progress',
    'Internal - Peer QC',
    'Internal - Final QC',
    'Sent for Customer Approval',
    'PLM Approval',
    'Completed',
];

export const TICKET_MAIN_FLOW_STEPS = [
    'Yet to Start',
    'In Progress',
    'Sent for Customer Approval',
    'PLM Approval',
    'Completed',
];

// Effort / utilization capacity -- matches the backend's utils/capacity.js.
export const WEEKLY_CAPACITY_HOURS = 40;
export const MONTHLY_CAPACITY_HOURS = 160;
export const YEARLY_CAPACITY_HOURS = MONTHLY_CAPACITY_HOURS * 12;

// Gates where an approve / reject decision applies.
export const DECISION_STATUSES = [
    'Internal - Peer QC',
    'Internal - Final QC',
    'Sent for Customer Approval',
    'PLM Approval',
    'Validation',
];

// Who's responsible for acting while a part/ticket sits in a given status
// (mirrors backend/src/utils/statusFlow.js getResponsibleParty). Once a
// status moves into the other bucket, the previous party is locked out.
// QC only owns the two internal design-review gates -- once Final QC is
// approved the assigned engineer takes it back through external feedback,
// PLM approval and completion. Tickets have no QC Engineer at all, so the
// assigned engineer stays responsible through every non-terminal status.
export const OWNER_STATUSES = ['Yet to Start', 'In Progress', 'On Hold', 'Sent for Customer Approval', 'PLM Approval'];
export const QC_STATUSES = ['Internal - Peer QC', 'Internal - Final QC'];
export const TICKET_OWNER_STATUSES = ['Yet to Start', 'In Progress', 'On Hold', 'Sent for Customer Approval', 'PLM Approval'];

export const STATUS_COLORS = {
    'Yet to Start':                  '#78909c',
    'In Progress':                   '#1e88e5',
    'Internal - Peer QC':            '#00897b',
    'Internal - Final QC':           '#00acc1',
    'Sent for Customer Approval':    '#f4511e',
    'PLM Approval':                  '#fb8c00',
    'Completed':                     '#43a047',
    'Validation':                    '#8e24aa',
    'On Hold':                       '#e53935',
    'Cancelled':                     '#9e9e9e',
};

export const PRIORITY_COLORS = {
    High:   '#e53935',
    Medium: '#fb8c00',
    Low:    '#43a047',
};

export const TASK_TYPE_COLORS = {
    PSG:          '#1b3f94',
    GPDS:         '#e8112d',
    'Other task': '#6d4c41',
    Training:      '#00897b',
    Documentation: '#5e35b1',
    Meeting:       '#f9a825',
};

// MUI Chip colour for a status.
export const statusChipColor = (status) => {
    switch (status) {
        case 'Completed':          return 'success';
        case 'On Hold':            return 'error';
        case 'Cancelled':          return 'default';
        case 'In Progress':        return 'primary';
        case 'PLM Approval':       return 'warning';
        case 'Validation':         return 'secondary';
        case 'Internal - Peer QC':
        case 'Internal - Final QC': return 'info';
        default:                    return 'secondary';
    }
};

export const priorityChipColor = (priority) =>
    priority === 'High' ? 'error' : priority === 'Medium' ? 'warning' : 'success';

// Roles that may assign work, approve and run reports.
export const MANAGER_ROLES = ['admin', 'head', 'manager'];
export const isManagerRole = (role) => MANAGER_ROLES.includes(role);

export const ROLE_LABELS = {
    admin:   'Admin',
    head:    'Head',
    manager: 'Manager',
    engineer: 'Engineer',
};

// Roles selectable when creating/editing a team member (admin is seed-only).
export const ROLE_OPTIONS = ['head', 'manager', 'engineer'];

/** Format a YYYY-MM-DD (or ISO) value as DD-Mon-YY, or '' when empty. */
export const fmtDate = (value) => {
    if (!value) return '';
    const d = new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

/** Hours as 00:00, matching the spec's "Estimated Hours 00:00" field. */
export const fmtHours = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (isNaN(n)) return '';
    const h = Math.floor(n);
    const m = Math.round((n - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** TAT is stored/computed in hours server-side; shown to users in days. */
export const fmtTatDays = (tatHours) => {
    if (tatHours === null || tatHours === undefined || tatHours === '') return null;
    const n = Number(tatHours);
    return isNaN(n) ? null : (n / 24).toFixed(1);
};
